import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { mostrarToast } from '@/lib/formularioGuiado';
import Sheet from '@/components/ui/Sheet';
import Botao from '@/components/ui/Botao';
import Campo from '@/components/ui/Campo';
import Chip from '@/components/ui/Chip';
import CampoArea from '@/components/base/CampoArea';
import type { OracaoAdmin } from '../types';
import { rotuloDiaCurto } from '../utils';
import { enviarImagem, enviarVideo, youtubeId } from '../oracaoMidia';

const TAM_AVISO = 160;

// Cores litúrgicas (o vazio deixa a cor padrão do app).
const CORES_LITURGICAS = ['verde', 'branco', 'vermelho', 'roxo', 'rosa', 'preto'];

interface FormOracaoProps {
  item: OracaoAdmin | null;
  aberto: boolean;
  aoFechar: () => void;
  aoAtualizar: () => void;
}

// Prévia do vídeo dentro da folha (YouTube sem cookies ou arquivo).
function PreviaVideo({ url }: { url: string }) {
  const yt = youtubeId(url);
  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200 bg-black">
      {yt ? (
        yt.shorts ? (
          <div className="mx-auto" style={{ maxHeight: 280, height: 280, aspectRatio: '9 / 16' }}>
            <iframe
              className="h-full w-full"
              src={`https://www.youtube-nocookie.com/embed/${yt.id}?playsinline=1&rel=0`}
              title="Prévia do vídeo"
              allowFullScreen
            />
          </div>
        ) : (
          <iframe
            className="aspect-video w-full"
            src={`https://www.youtube-nocookie.com/embed/${yt.id}?playsinline=1&rel=0`}
            title="Prévia do vídeo"
            allowFullScreen
          />
        )
      ) : (
        <video
          className="mx-auto block"
          controls
          playsInline
          preload="metadata"
          src={`${url}#t=0.1`}
          style={{ maxHeight: 280, maxWidth: '100%' }}
        />
      )}
    </div>
  );
}

// Prévia da imagem em moldura de arco.
function PreviaImagem({ url }: { url: string }) {
  return (
    <div className="mx-auto max-w-[200px] overflow-hidden rounded-b-[14px] rounded-t-[999px] border border-[rgba(217,178,95,.55)] bg-secondary-100">
      <img src={url} alt="Imagem do dia" className="block aspect-[4/5] w-full object-cover" />
    </div>
  );
}

// Folha para editar, aprovar e pré-visualizar um dia de oração.
export default function FormOracao({ item, aberto, aoFechar, aoAtualizar }: FormOracaoProps) {
  const navigate = useNavigate();
  const dia = item?.dia ?? null;

  const [tempoLiturgico, setTempoLiturgico] = useState('');
  const [corLiturgica, setCorLiturgica] = useState('');
  const [santo, setSanto] = useState('');
  const [santoTitulo, setSantoTitulo] = useState('');
  const [santoTexto, setSantoTexto] = useState('');
  const [santoPedido, setSantoPedido] = useState('');
  const [evangelhoRef, setEvangelhoRef] = useState('');
  const [leituraGuia, setLeituraGuia] = useState('');
  const [tema, setTema] = useState('');
  const [resumo, setResumo] = useState('');
  const [reflexao, setReflexao] = useState('');
  const [perguntas, setPerguntas] = useState('');
  const [texto, setTexto] = useState('');
  const [silencio, setSilencio] = useState('');
  const [gesto, setGesto] = useState('');
  const [catecismo, setCatecismo] = useState('');
  const [aviso, setAviso] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [imagemCredito, setImagemCredito] = useState('');
  const [imagemLink, setImagemLink] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [videoAutor, setVideoAutor] = useState('');
  const [videoLink, setVideoLink] = useState('');

  const [salvando, setSalvando] = useState(false);
  const [mudandoAprovacao, setMudandoAprovacao] = useState(false);
  const [enviandoVideo, setEnviandoVideo] = useState(false);
  const [progressoVideo, setProgressoVideo] = useState<number | null>(null);
  const [enviandoImagem, setEnviandoImagem] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const inputVideoRef = useRef<HTMLInputElement>(null);
  const inputImagemRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!aberto || !item) return;
    setTempoLiturgico(item.tempo_liturgico ?? '');
    setCorLiturgica(item.cor_liturgica ?? '');
    setSanto(item.santo ?? '');
    setSantoTitulo(item.santo_titulo ?? '');
    setSantoTexto(item.santo_texto ?? '');
    setSantoPedido(item.santo_pedido ?? '');
    setEvangelhoRef(item.evangelho_ref ?? '');
    setLeituraGuia(item.leitura_guia ?? '');
    setTema(item.tema ?? '');
    setResumo(item.resumo ?? '');
    setReflexao(item.reflexao ?? '');
    setPerguntas(item.perguntas ?? '');
    setTexto(item.texto ?? '');
    setSilencio(item.silencio ?? '');
    setGesto(item.gesto ?? '');
    setCatecismo(item.catecismo ?? '');
    setAviso(item.aviso ?? '');
    setImagemUrl(item.imagem_url ?? '');
    setImagemCredito(item.imagem_credito ?? '');
    setImagemLink('');
    setVideoUrl(item.video_url ?? '');
    setVideoAutor(item.video_autor ?? '');
    setVideoLink('');
    setErro(null);
  }, [aberto, item]);

  if (!item || !dia) return null;

  async function salvar(
    over?: { imagem_url?: string; video_url?: string },
    avisarSalvo = true,
  ) {
    if (!dia) return;
    const imagemFinal = (over?.imagem_url !== undefined
      ? over.imagem_url
      : imagemUrl || imagemLink
    ).trim();
    const videoFinal = (over?.video_url !== undefined
      ? over.video_url
      : videoUrl || videoLink
    ).trim();

    if (imagemFinal && !imagemFinal.startsWith('https://')) {
      setErro('O link precisa começar com https://');
      return;
    }
    if (videoFinal && !videoFinal.startsWith('https://')) {
      setErro('O link precisa começar com https://');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('oracoes_admin_salvar', {
        p_dia: dia,
        p_evangelho_ref: evangelhoRef.trim(),
        p_tema: tema.trim(),
        p_resumo: resumo.trim(),
        p_silencio: silencio.trim(),
        p_texto: texto.trim(),
        p_gesto: gesto.trim(),
        p_aviso: aviso.trim(),
        p_tempo_liturgico: tempoLiturgico.trim(),
        p_cor_liturgica: corLiturgica.trim(),
        p_santo: santo.trim(),
        p_santo_titulo: santoTitulo.trim(),
        p_santo_texto: santoTexto.trim(),
        p_santo_pedido: santoPedido.trim(),
        p_reflexao: reflexao.trim(),
        p_catecismo: catecismo.trim(),
        p_leitura_guia: leituraGuia.trim(),
        p_perguntas: perguntas.trim(),
        p_imagem_url: imagemFinal,
        p_imagem_credito: imagemCredito.trim(),
        p_video_url: videoFinal,
        p_video_autor: videoAutor.trim(),
      });
      if (error) throw error;
      setImagemUrl(imagemFinal);
      setImagemLink('');
      setVideoUrl(videoFinal);
      setVideoLink('');
      if (avisarSalvo) mostrarToast('Oração salva.', 3500, 'sucesso');
      aoAtualizar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setSalvando(false);
    }
  }

  async function aprovar(valor: boolean) {
    if (!dia) return;
    setMudandoAprovacao(true);
    setErro(null);
    try {
      const { error } = await supabase.rpc('oracoes_admin_aprovar', {
        p_dias: [dia],
        p_aprovar: valor,
      });
      if (error) throw error;
      mostrarToast(valor ? 'Aprovada.' : 'Aprovação retirada.', 3500, 'sucesso');
      aoAtualizar();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setMudandoAprovacao(false);
    }
  }

  async function escolherVideo(arquivo: File) {
    if (!dia) return;
    setErro(null);
    setEnviandoVideo(true);
    setProgressoVideo(0);
    try {
      const url = await enviarVideo(arquivo, dia, setProgressoVideo);
      setVideoUrl(url);
      setVideoLink('');
      await salvar({ video_url: url }, false);
      mostrarToast('Vídeo enviado e salvo.', 3500, 'sucesso');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos enviar o vídeo agora.'));
    } finally {
      setEnviandoVideo(false);
      setProgressoVideo(null);
    }
  }

  async function escolherImagem(arquivo: File) {
    if (!dia) return;
    setErro(null);
    setEnviandoImagem(true);
    try {
      const url = await enviarImagem(arquivo, dia);
      setImagemUrl(url);
      setImagemLink('');
      await salvar({ imagem_url: url }, false);
      mostrarToast('Imagem enviada e salva.', 3500, 'sucesso');
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos enviar a imagem agora.'));
    } finally {
      setEnviandoImagem(false);
    }
  }

  async function removerVideo() {
    setVideoUrl('');
    setVideoLink('');
    await salvar({ video_url: '' }, false);
    mostrarToast('Vídeo removido.', 3000, 'sucesso');
  }

  async function removerImagem() {
    setImagemUrl('');
    setImagemLink('');
    await salvar({ imagem_url: '' }, false);
    mostrarToast('Imagem removida.', 3000, 'sucesso');
  }

  function verComoFica() {
    if (!dia) return;
    navigate(`/oracao?dia=${dia}`);
  }

  const ocupado = enviandoVideo || enviandoImagem;

  return (
    <Sheet
      aberto={aberto}
      titulo={`Oração · ${rotuloDiaCurto(dia)}`}
      aoFechar={aoFechar}
      rodape={
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 sm:flex-row">
            <Botao
              cheio
              variante="primario"
              icone="ri-save-3-line"
              carregando={salvando}
              disabled={ocupado}
              onClick={() => void salvar()}
            >
              Salvar
            </Botao>
            <Botao
              cheio
              variante={item.aprovada ? 'secundario' : 'primario'}
              icone={item.aprovada ? 'ri-close-circle-line' : 'ri-checkbox-circle-line'}
              carregando={mudandoAprovacao}
              onClick={() => void aprovar(!item.aprovada)}
            >
              {item.aprovada ? 'Tirar aprovação' : 'Aprovar'}
            </Botao>
          </div>
          <Botao cheio variante="fantasma" icone="ri-eye-line" onClick={verComoFica}>
            Ver como fica
          </Botao>
        </div>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <Chip tipo={item.aprovada ? 'success' : 'warning'}>
            {item.aprovada ? 'Aprovada' : 'Rascunho'}
          </Chip>
          <span className="text-xs text-foreground-500">
            {item.aprovada_em
              ? `aprovada em ${rotuloDiaCurto(item.aprovada_em.slice(0, 10))}`
              : 'ainda não aprovada'}
          </span>
        </div>

        {/* Vídeo do padre */}
        <div className="rounded-xl border border-secondary-200 bg-background-50 p-4">
          <p className="text-sm font-semibold text-foreground-800">Vídeo do padre (opcional)</p>

          {videoUrl ? (
            <div className="mt-3 space-y-3">
              <PreviaVideo url={videoUrl} />
              <Campo
                rotulo="Quem fala no vídeo"
                value={videoAutor}
                maxLength={160}
                onChange={setVideoAutor}
                placeholder="Ex.: Pe. João Silva, Paróquia São José"
              />
              <div className="flex flex-wrap gap-2">
                <Botao
                  variante="secundario"
                  icone="ri-movie-2-line"
                  disabled={enviandoVideo}
                  onClick={() => inputVideoRef.current?.click()}
                >
                  Trocar vídeo
                </Botao>
                <Botao
                  variante="perigo-contorno"
                  icone="ri-delete-bin-line"
                  disabled={salvando}
                  onClick={() => void removerVideo()}
                >
                  Remover vídeo
                </Botao>
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <Botao
                cheio
                variante="secundario"
                icone="ri-upload-2-line"
                carregando={enviandoVideo}
                onClick={() => inputVideoRef.current?.click()}
              >
                Enviar vídeo do celular
              </Botao>
              <Campo
                rotulo="ou cole um link do YouTube"
                value={videoLink}
                onChange={setVideoLink}
                placeholder="https://youtu.be/..."
              />
            </div>
          )}

          {enviandoVideo && progressoVideo !== null && (
            <div className="mt-3">
              <div className="h-2 w-full overflow-hidden rounded-full bg-secondary-200">
                <div
                  className="h-full rounded-full bg-primary-500 transition-all"
                  style={{ width: `${progressoVideo}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-foreground-500">
                Enviando… {progressoVideo}% — não feche esta tela.
              </p>
            </div>
          )}

          <p className="mt-3 text-xs text-foreground-500">
            Grave na vertical, de 1 a 3 minutos. Arquivo até 50 MB. Para vídeos maiores, publique no
            YouTube como “Não listado” e cole o link aqui.
          </p>

          <input
            ref={inputVideoRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm"
            className="hidden"
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              ev.target.value = '';
              if (f) void escolherVideo(f);
            }}
          />
        </div>

        {/* Imagem do dia */}
        <div className="rounded-xl border border-secondary-200 bg-background-50 p-4">
          <p className="text-sm font-semibold text-foreground-800">Imagem do dia (opcional)</p>

          {imagemUrl ? (
            <div className="mt-3 space-y-3">
              <PreviaImagem url={imagemUrl} />
              <Campo
                rotulo="Crédito da imagem"
                value={imagemCredito}
                maxLength={200}
                onChange={setImagemCredito}
                placeholder="Ex.: Rubens, 1615 · domínio público"
              />
              <div className="flex flex-wrap gap-2">
                <Botao
                  variante="secundario"
                  icone="ri-image-2-line"
                  disabled={enviandoImagem}
                  onClick={() => inputImagemRef.current?.click()}
                >
                  Trocar imagem
                </Botao>
                <Botao
                  variante="perigo-contorno"
                  icone="ri-delete-bin-line"
                  disabled={salvando}
                  onClick={() => void removerImagem()}
                >
                  Remover imagem
                </Botao>
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <Botao
                cheio
                variante="secundario"
                icone="ri-upload-2-line"
                carregando={enviandoImagem}
                onClick={() => inputImagemRef.current?.click()}
              >
                Enviar imagem
              </Botao>
              <Campo
                rotulo="ou cole o link da imagem"
                value={imagemLink}
                onChange={setImagemLink}
                placeholder="https://..."
              />
            </div>
          )}

          <p className="mt-3 text-xs text-foreground-500">
            Use obras de domínio público ou fotos suas, sempre com o crédito. Sem imagem, a oração
            usa a arte padrão.
          </p>

          <input
            ref={inputImagemRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(ev) => {
              const f = ev.target.files?.[0];
              ev.target.value = '';
              if (f) void escolherImagem(f);
            }}
          />
        </div>

        <Campo
          rotulo="Tempo litúrgico"
          value={tempoLiturgico}
          maxLength={120}
          onChange={setTempoLiturgico}
          placeholder="Ex.: Tempo comum, 28ª semana"
        />

        <div className="w-full">
          <label
            htmlFor="oracao-cor-liturgica"
            className="mb-1.5 block text-sm font-medium text-foreground-800"
          >
            Cor litúrgica
          </label>
          <select
            id="oracao-cor-liturgica"
            name="cor_liturgica"
            value={corLiturgica}
            onChange={(ev) => setCorLiturgica(ev.target.value)}
            className="h-12 w-full cursor-pointer rounded-xl border border-secondary-300 bg-white px-3.5 text-base text-foreground-950 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
          >
            <option value="">Sem cor</option>
            {CORES_LITURGICAS.map((cor) => (
              <option key={cor} value={cor}>
                {cor.charAt(0).toUpperCase() + cor.slice(1)}
              </option>
            ))}
          </select>
        </div>

        <Campo
          rotulo="Santo do dia"
          value={santo}
          maxLength={120}
          onChange={setSanto}
          placeholder="Ex.: Santa Teresa de Ávila"
        />

        <Campo
          rotulo="Título do santo"
          value={santoTitulo}
          maxLength={160}
          onChange={setSantoTitulo}
          placeholder="Ex.: Virgem e Doutora da Igreja"
        />

        <CampoArea
          id="oracao-santo-texto"
          name="santo_texto"
          label="Quem foi o santo"
          value={santoTexto}
          onChange={setSantoTexto}
          maxLength={1200}
          simples
          placeholder="Um resumo curto da vida do santo."
        />

        <CampoArea
          id="oracao-santo-pedido"
          name="santo_pedido"
          label="Pedido ao santo"
          value={santoPedido}
          onChange={setSantoPedido}
          maxLength={500}
          simples
          placeholder="Uma frase de pedido ao santo do dia."
        />

        <Campo
          rotulo="Referência"
          value={evangelhoRef}
          maxLength={120}
          onChange={setEvangelhoRef}
          placeholder="Ex.: Lc 10, 25-37"
        />

        <Campo
          rotulo="Onde achar na Bíblia"
          value={leituraGuia}
          maxLength={400}
          onChange={setLeituraGuia}
          placeholder="Ex.: Evangelho de Lucas, capítulo 10, versículos 25 a 37"
        />

        <Campo
          rotulo="Tema"
          value={tema}
          maxLength={120}
          onChange={setTema}
          placeholder="O tema do dia"
        />

        <CampoArea
          id="oracao-resumo"
          name="resumo"
          label="O Evangelho de hoje"
          value={resumo}
          onChange={setResumo}
          maxLength={1200}
          simples
          placeholder="Um resumo curto do Evangelho do dia."
        />

        <CampoArea
          id="oracao-reflexao"
          name="reflexao"
          label="Meditação"
          value={reflexao}
          onChange={setReflexao}
          maxLength={1500}
          simples
          placeholder="A meditação do dia."
        />

        <CampoArea
          id="oracao-perguntas"
          name="perguntas"
          label="Perguntas"
          value={perguntas}
          onChange={setPerguntas}
          maxLength={700}
          simples
          placeholder="Uma pergunta por linha."
        />

        <CampoArea
          id="oracao-texto"
          name="texto"
          label="Oração"
          value={texto}
          onChange={setTexto}
          maxLength={4000}
          simples
          placeholder="O texto da oração. As quebras de linha são mantidas."
        />

        <CampoArea
          id="oracao-silencio"
          name="silencio"
          label="Um minuto de silêncio"
          value={silencio}
          onChange={setSilencio}
          maxLength={800}
          simples
          placeholder="O que pensar no minuto de silêncio."
        />

        <CampoArea
          id="oracao-gesto"
          name="gesto"
          label="Desafio do dia"
          value={gesto}
          onChange={setGesto}
          maxLength={800}
          simples
          placeholder="Um gesto concreto para o dia."
        />

        <CampoArea
          id="oracao-catecismo"
          name="catecismo"
          label="Para aprofundar (Catecismo)"
          value={catecismo}
          onChange={setCatecismo}
          maxLength={500}
          simples
          placeholder="Uma referência do Catecismo para quem quiser ir além."
        />

        <div>
          <CampoArea
            id="oracao-aviso"
            name="aviso"
            label="Convite do aviso"
            value={aviso}
            onChange={setAviso}
            maxLength={TAM_AVISO}
            simples
            placeholder="A frase que aparece no aviso das 6h."
          />
          <p className="mt-1 text-right text-xs text-foreground-500">
            {aviso.length}/{TAM_AVISO}
          </p>
        </div>

        {erro && (
          <p className="flex items-start gap-1.5 text-sm text-danger-600" role="alert">
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
              <i className="ri-error-warning-line" aria-hidden />
            </span>
            <span className="min-w-0">{erro}</span>
          </p>
        )}
      </div>
    </Sheet>
  );
}